import CryptoJS from 'crypto-js'
import NodeRSA from 'node-rsa'

/**
 * 加密工具类
 * 提供RSA和AES加密解密功能
 */
class CryptoUtils {
  constructor() {
    // RSA公钥（用于加密发送给后端的数据）
    this.encryptPublicKey = `-----BEGIN PUBLIC KEY-----
MFwwDQYJKoZIhvcNAQEBBQADSwAwSAJBAL+3HHwHLOLvM9k3tM3Jed7N9jVGkLKx
g/ADoD1oT1K985tjDCxB7upqrUW4dL2Wk+5k2GEW0nwHupIaqCHIoH0CAwEAAQ==
-----END PUBLIC KEY-----`
    
    // RSA公钥（用于解密后端返回的数据）
    this.decryptPublicKey = `-----BEGIN PUBLIC KEY-----
MFwwDQYJKoZIhvcNAQEBBQADSwAwSAJBAMGazVfBgmjpjYQtjb56htTFJgCJqMnX
nSzXrvb/+I48/WrcbdrI3/hhS/3CT5c0D7UIhIUOu97vNjgfbn+s/gUCAwEAAQ==
-----END PUBLIC KEY-----`
    
    // AES密钥
    this.aesKey = 'HongChen'
    
    // 签名后缀
    this.signatureSuffix = 'HongChenShuaiGe'
  }

  /**
   * 生成当前时间戳
   * @returns {number} 时间戳
   */
  getCurrentTimestamp() {
    return Math.floor(Date.now() / 1000)
  }

  /**
   * 创建签名字符串
   * @param {number} timestamp - 时间戳
   * @param {string} ip - IP地址
   * @returns {string} 签名字符串
   */
  createSignature(timestamp, ip) {
    return `${timestamp}|${ip}|${this.signatureSuffix}`
  }

  /**
   * RSA公钥加密
   * @param {string} data - 要加密的数据
   * @returns {string} 加密后的数据
   */
  rsaEncrypt(data) {
    try {
      const rsa = new NodeRSA(this.encryptPublicKey)
      rsa.setOptions({ encryptionScheme: 'pkcs1' })
      return rsa.encrypt(data, 'base64')
    } catch (error) {
      throw new Error('RSA加密失败')
    }
  }

  /**
   * RSA公钥解密（用于验证后端的私钥签名）
   * @param {string} encryptedData - 加密的数据
   * @returns {string} 解密后的数据
   */
  rsaDecrypt(encryptedData) {
    try {
      // 使用解密公钥验证后端的私钥签名
      const rsa = new NodeRSA(this.decryptPublicKey)
      rsa.setOptions({ 
        encryptionScheme: 'pkcs1',
        signingScheme: 'pkcs1'
      })
      
      return rsa.decryptPublic(encryptedData, 'utf8')
    } catch (error) {
      // 如果公钥解密失败，尝试普通解密
      try {
        const rsa2 = new NodeRSA(this.decryptPublicKey)
        rsa2.setOptions({ encryptionScheme: 'pkcs1' })
        return rsa2.decrypt(encryptedData, 'utf8')
      } catch (error2) {
        throw new Error('RSA解密失败')
      }
    }
  }

  /**
   * 验证解密后的数据格式和内容
   * @param {string} decryptedData - 解密后的数据
   * @param {string} currentIp - 当前IP地址
   * @returns {object} 验证结果
   */
  validateDecryptedData(decryptedData, currentIp) {
    try {
      // 解析数据格式: timestamp|authorized|ip|HongChenShuaiGe
      const parts = decryptedData.split('|')
      if (parts.length !== 4) {
        return { success: false, message: '数据格式错误' }
      }
      
      const [timestamp, authorized, ip, signature] = parts
      
      // 检查是否包含authorized
      if (authorized !== 'authorized') {
        return { success: false, message: '授权标识错误' }
      }
      
      // 检查签名后缀
      if (signature !== 'HongChenShuaiGe') {
        return { success: false, message: '签名错误' }
      }
      
      // 检查IP是否匹配
      if (ip !== currentIp) {
        return { success: false, message: 'IP不匹配' }
      }
      
      // 检查时间戳（10秒内）
      const serverTimestamp = parseInt(timestamp)
      const currentTimestamp = Math.floor(Date.now() / 1000)
      const timeDiff = Math.abs(currentTimestamp - serverTimestamp)
      
      if (timeDiff > 10) {
        return { success: false, message: '时间戳超时' }
      }
      
      return { success: true, message: '验证通过' }
      
    } catch (error) {
      return { success: false, message: '验证异常' }
    }
  }

  /**
   * AES加密 - 与Java后端AES/ECB/PKCS5Padding兼容
   * @param {string} data - 要加密的数据
   * @param {string} key - 加密密钥，默认使用HongChen
   * @returns {string} 加密后的数据（Base64编码）
   */
  aesEncrypt(data, key = this.aesKey) {
    try {
      // 将密钥转换为WordArray，确保长度为16字节（128位）
      const keyBytes = CryptoJS.enc.Utf8.parse(key.padEnd(16, '\0').substring(0, 16))
      
      // 使用ECB模式和PKCS7填充（等同于Java的PKCS5Padding）
      const encrypted = CryptoJS.AES.encrypt(data, keyBytes, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      })
      
      // 返回Base64编码的密文
      return encrypted.toString()
    } catch (error) {
      console.error('AES加密失败:', error)
      throw new Error('AES加密失败')
    }
  }

  /**
   * AES解密 - 与Java后端AES/ECB/PKCS5Padding兼容
   * @param {string} encryptedData - 加密的数据（Base64编码）
   * @param {string} key - 解密密钥，默认使用HongChen
   * @returns {string} 解密后的数据
   */
  aesDecrypt(encryptedData, key = this.aesKey) {
    try {
      // 将密钥转换为WordArray，确保长度为16字节（128位）
      const keyBytes = CryptoJS.enc.Utf8.parse(key.padEnd(16, '\0').substring(0, 16))
      
      // 使用ECB模式和PKCS7填充进行解密
      const decrypted = CryptoJS.AES.decrypt(encryptedData, keyBytes, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      })
      
      // 返回解密后的UTF-8字符串
      return decrypted.toString(CryptoJS.enc.Utf8)
    } catch (error) {
      console.error('AES解密失败:', error)
      throw new Error('AES解密失败')
    }
  }

  /**
   * 创建加密的请求参数 - 简化版本，只用RSA加密
   * @param {string} ip - IP地址
   * @returns {string} 加密后的请求参数
   */
  createEncryptedPayload(ip) {
    try {
      // 1. 生成时间戳
      const timestamp = this.getCurrentTimestamp()
      
      // 2. 创建签名字符串
      const signature = this.createSignature(timestamp, ip)
      
      // 3. 只用RSA公钥加密签名
      const rsaEncrypted = this.rsaEncrypt(signature)
      
      return rsaEncrypted
    } catch (error) {
      throw new Error('创建加密参数失败')
    }
  }

  /**
   * 解密并验证后端返回的数据
   * @param {string} encryptedResponse - 后端返回的加密数据
   * @returns {object} 验证结果
   */
  decryptAndVerifyResponse(encryptedResponse) {
    try {
      // 1. 使用HongChen密钥AES解密
      const aesDecrypted = this.aesDecrypt(encryptedResponse)
      
      // 2. RSA解密
      const rsaDecrypted = this.rsaDecrypt(aesDecrypted)
      
      // 3. 解析返回的数据 (假设格式为: timestamp|result|signature)
      const parts = rsaDecrypted.split('|')
      if (parts.length < 2) {
        throw new Error('返回数据格式错误')
      }
      
      const responseTimestamp = parseInt(parts[0])
      const result = parts[1]
      const responseSignature = parts.slice(2).join('|')
      
      // 4. 验证时间戳是否在10秒内
      const currentTimestamp = this.getCurrentTimestamp()
      const timeDiff = Math.abs(currentTimestamp - responseTimestamp)
      
      if (timeDiff > 10) {
        throw new Error('响应时间戳超时')
      }
      
      // 5. 验证签名
      if (responseSignature !== this.signatureSuffix) {
        throw new Error('签名验证失败')
      }
      
      return {
        success: true,
        result: result,
        timestamp: responseTimestamp,
        message: '验证成功'
      }
    } catch (error) {
      console.error('解密验证失败:', error)
      return {
        success: false,
        error: error.message,
        message: '解密验证失败'
      }
    }
  }

  /**
   * 验证时间戳是否有效（10秒内）
   * @param {number} timestamp - 要验证的时间戳
   * @returns {boolean} 是否有效
   */
  isTimestampValid(timestamp) {
    const currentTimestamp = this.getCurrentTimestamp()
    const timeDiff = Math.abs(currentTimestamp - timestamp)
    return timeDiff <= 10
  }
}

export default CryptoUtils



// WEBPACK FOOTER //
// ./src/utils/cryptoUtils.js